import { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';

import { useAppState, useIsFocused } from '@garnish/core';

// ──────────────────────────────────────────────────────────────────────────

/**
 * Start/stop cart polling on bag focus.
 * Refetch cart on app focus if the bag has focus.
 */
export const useBagCartPollingControl = (params: Params) => {
  const { startBagCartPolling, stopBagCartPolling } = params;

  const isFocused = useIsFocused();

  // ─── Handlers ────────────────────────────────────────────────────────

  const togglePolling = useCallback(() => {
    startBagCartPolling();

    return () => {
      stopBagCartPolling();
    };
  }, [startBagCartPolling, stopBagCartPolling]);

  const refetchOnVisible = useCallback(() => {
    if (!isFocused) return;

    startBagCartPolling();
  }, [startBagCartPolling, isFocused]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(togglePolling);

  useAppState({
    onVisible: refetchOnVisible,
  });
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  startBagCartPolling: () => void;
  stopBagCartPolling: () => void;
};
